/**
 * 任务中列表 / 临时分组持久化
 * 权威存储：指挥台共享 /api/command-mission-state（跨登录账号）
 * 兼容：仍镜像写入当前用户 user_settings（旧客户端可读）；localStorage 用全局键，不按 userId 隔离
 */
import { saveUserSettings, SETTING_KEYS } from '../userSettings';
import apiFetch from '../apiFetch';
import { canEditMissionBoard } from '../permissionControl';
import { missionIdMatchesMember, sanitizeMissionMemberIds } from './deviceIdentity';

export const COMMAND_MISSION_BOARD_LOCAL_KEY = 'command_mission_board';
/** 多端同步：短轮询间隔（配合 WS command_mission_board_updated） */
export const BOARD_POLL_MS = 12000;


export const MISSION_BOARD_SETTING_KEYS = [
  SETTING_KEYS.MISSION_MEMBER_IDS,
  SETTING_KEYS.MISSION_GROUPS,
  SETTING_KEYS.MISSION_MODE,
  SETTING_KEYS.MISSIONS,
  SETTING_KEYS.ACTIVE_MISSION_ID
];

function buildBoardState({
  missionMemberIds = [],
  missionGroups = [],
  missionMode = false,
  missions = null,
  activeMissionId = null
} = {}) {
  const ids = Array.isArray(missionMemberIds)
    ? missionMemberIds.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const groups = Array.isArray(missionGroups) ? missionGroups : [];
  const list = Array.isArray(missions) ? missions : null;
  const mode = list
    ? (list.length > 0 && list.some((m) => (m?.memberIds || []).length > 0))
    : (Boolean(missionMode) && ids.length > 0);
  const state = {
    [SETTING_KEYS.MISSION_MEMBER_IDS]: ids,
    [SETTING_KEYS.MISSION_GROUPS]: groups,
    [SETTING_KEYS.MISSION_MODE]: mode
  };
  if (list) {
    state[SETTING_KEYS.MISSIONS] = list;
    state[SETTING_KEYS.ACTIVE_MISSION_ID] = activeMissionId || (list[0] && list[0].id) || null;
  } else {
    state[SETTING_KEYS.MISSIONS] = [];
    state[SETTING_KEYS.ACTIVE_MISSION_ID] = activeMissionId || null;
  }
  return state;
}

function boardHasData(state) {
  if (!state || typeof state !== 'object') return false;
  const missions = state[SETTING_KEYS.MISSIONS] || state.missions;
  const groups = state[SETTING_KEYS.MISSION_GROUPS] || state.missionGroups;
  const ids = state[SETTING_KEYS.MISSION_MEMBER_IDS] || state.missionMemberIds;
  return (Array.isArray(missions) && missions.length > 0)
    || (Array.isArray(groups) && groups.length > 0)
    || (Array.isArray(ids) && ids.length > 0);
}

function readLocalBoard() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COMMAND_MISSION_BOARD_LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeLocalBoard(state) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(COMMAND_MISSION_BOARD_LOCAL_KEY, JSON.stringify(state || {}));
  } catch (_) { /* ignore */ }
}

/** 立即写入共享看板（+ 本地全局缓存 + 镜像当前用户 settings） */
export async function persistMissionState({
  missionMemberIds = [],
  missionGroups = [],
  missionMode = false,
  memberPanelTab = 'all',
  missions = null,
  activeMissionId = null
} = {}) {
  if (!canEditMissionBoard()) {
    console.warn('[mission] 无任务看板编辑权限，跳过写入');
    return;
  }

  const board = buildBoardState({
    missionMemberIds,
    missionGroups,
    missionMode,
    missions,
    activeMissionId
  });
  writeLocalBoard(board);

  // 个人 settings 只保留 UI 偏好（标签），不再镜像 mission*（权威=共享表）
  const personalUiOnly = {
    [SETTING_KEYS.MEMBER_PANEL_TAB]: ['all', 'mission', 'playback'].includes(memberPanelTab)
      ? memberPanelTab
      : (board[SETTING_KEYS.MISSION_MODE] ? 'mission' : 'all')
  };

  try {
    const res = await apiFetch('/api/command-mission-state', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: board })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      console.warn('[mission] persist shared board failed:', data.error || res.status);
      if (res.status === 403) {
        const err = new Error(data.error || '无权限编辑任务看板');
        err.code = 'PERMISSION_DENIED';
        err.status = 403;
        throw err;
      }
    }
  } catch (err) {
    console.warn('[mission] persist shared board failed:', err?.message || err);
    if (err && err.status === 403) throw err;
  }

  try {
    await saveUserSettings(personalUiOnly);
  } catch (err) {
    console.warn('[mission] save UI prefs failed:', err?.message || err);
  }
}

/**
 * 加载指挥台共享任务看板。
 * 优先服务端共享表 → 本地全局缓存 → 个人 settings 兜底（并回灌共享）。
 */
export async function loadCommandMissionState(fallbackSettings = null) {
  let state = null;

  try {
    const res = await apiFetch('/api/command-mission-state');
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.success && data.state) {
      state = buildBoardState({
        missionMemberIds: data.state.missionMemberIds,
        missionGroups: data.state.missionGroups,
        missionMode: data.state.missionMode,
        missions: data.state.missions,
        activeMissionId: data.state.activeMissionId
      });
    }
  } catch (err) {
    console.warn('[mission] load shared board failed:', err?.message || err);
  }

  if (!boardHasData(state)) {
    const local = readLocalBoard();
    if (boardHasData(local)) {
      state = buildBoardState({
        missionMemberIds: local[SETTING_KEYS.MISSION_MEMBER_IDS] || local.missionMemberIds,
        missionGroups: local[SETTING_KEYS.MISSION_GROUPS] || local.missionGroups,
        missionMode: local[SETTING_KEYS.MISSION_MODE] ?? local.missionMode,
        missions: local[SETTING_KEYS.MISSIONS] || local.missions,
        activeMissionId: local[SETTING_KEYS.ACTIVE_MISSION_ID] || local.activeMissionId
      });
    }
  }

  if (!boardHasData(state) && fallbackSettings && typeof fallbackSettings === 'object') {
    const fb = buildBoardState({
      missionMemberIds: fallbackSettings[SETTING_KEYS.MISSION_MEMBER_IDS],
      missionGroups: fallbackSettings[SETTING_KEYS.MISSION_GROUPS],
      missionMode: fallbackSettings[SETTING_KEYS.MISSION_MODE],
      missions: fallbackSettings[SETTING_KEYS.MISSIONS],
      activeMissionId: fallbackSettings[SETTING_KEYS.ACTIVE_MISSION_ID]
    });
    if (boardHasData(fb)) {
      state = fb;
      // 个人里有、共享还空：回灌一次，换号后也能读到
      try {
        await persistMissionState({
          missionMemberIds: fb[SETTING_KEYS.MISSION_MEMBER_IDS],
          missionGroups: fb[SETTING_KEYS.MISSION_GROUPS],
          missionMode: fb[SETTING_KEYS.MISSION_MODE],
          missions: fb[SETTING_KEYS.MISSIONS],
          activeMissionId: fb[SETTING_KEYS.ACTIVE_MISSION_ID],
          memberPanelTab: fallbackSettings[SETTING_KEYS.MEMBER_PANEL_TAB] || 'all'
        });
      } catch (_) { /* ignore */ }
    }
  }

  if (boardHasData(state)) {
    writeLocalBoard(state);
    return state;
  }
  return buildBoardState({});
}

/** 从 tracker_bind_tokens.allow_voice_ptt 还原任务中列表成员 id */
export async function fetchMissionMemberIdsFromVoicePtt() {
  const res = await apiFetch('/api/tracker/list');
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const ids = [];
  const seen = new Set();
  (data.tokens || []).forEach((t) => {
    if (!(t?.allow_voice_ptt === true || t?.allow_voice_ptt === 't' || t?.allow_voice_ptt === 1)) return;
    const id = String(t.device_unique_id || t.name || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

/** 令牌 id / 设备 id / 显示名是否视为同一任务成员 */
export function missionIdInAllowList(memberId, missionMemberIds) {
  const mid = String(memberId || '').trim();
  if (!mid) return false;
  const allow = Array.isArray(missionMemberIds) ? missionMemberIds : [];
  if (allow.includes(mid)) return true;
  return allow.some((aid) => (
    missionIdMatchesMember(aid, { device_unique_id: mid, name: mid })
    || missionIdMatchesMember(mid, { device_unique_id: aid, name: aid })
  ));
}

/** 把分组里存的旧 id 规范成当前任务列表里的 canonical id */
export function canonicalMissionMemberId(memberId, missionMemberIds) {
  const mid = String(memberId || '').trim();
  if (!mid) return mid;
  const allow = Array.isArray(missionMemberIds) ? missionMemberIds : [];
  const hit = allow.find((aid) => (
    aid === mid
    || missionIdMatchesMember(aid, { device_unique_id: mid, name: mid })
    || missionIdMatchesMember(mid, { device_unique_id: aid, name: aid })
  ));
  return hit || mid;
}

export function missionIdListsEquivalent(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;
  return left.every((id) => missionIdInAllowList(id, right))
    && right.every((id) => missionIdInAllowList(id, left));
}

/**
 * 任务分组只保留仍在任务中的成员（模糊匹配，避免 token/设备 id 重映射后被踢出分组）。
 * 离线不改变 allow 列表，故不会因离线被 prune。
 */
export function pruneMissionGroups(groups, missionMemberIds, liveMembers = []) {
  const allow = Array.isArray(missionMemberIds) ? missionMemberIds : [];
  const live = Array.isArray(liveMembers) ? liveMembers : [];
  return (Array.isArray(groups) ? groups : []).map((g) => {
    const filtered = [];
    (g.memberIds || []).forEach((raw) => {
      if (!missionIdInAllowList(raw, allow)) return;
      filtered.push(raw);
    });
    const canonical = live.length
      ? sanitizeMissionMemberIds(filtered, live)
      : (() => {
        const seen = new Set();
        const out = [];
        filtered.forEach((raw) => {
          const id = canonicalMissionMemberId(raw, allow);
          if (!id || seen.has(id)) return;
          seen.add(id);
          out.push(id);
        });
        return out;
      })();
    return { ...g, memberIds: canonical };
  });
}
